/**
 * 30-day line charts for the Admin Performance tab (Web Vitals, CLS, page loads, sessions).
 */

import React, { useMemo } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { format, parseISO } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { HistoryDaySlot } from '../../services/performanceAggregationService';

const MS_WEB_VITALS = ['LCP', 'FID', 'FCP', 'TTFB', 'INP'] as const;

interface PerformanceHistoryChartsProps {
  historySlots: HistoryDaySlot[];
  pageLabelForKey: (pageKey: string) => string;
}

function chartPalette(theme: ReturnType<typeof useTheme>, count: number): string[] {
  const base = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  const extras = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1', '#d084d0'];
  const all = [...base, ...extras];
  return Array.from({ length: count }, (_, i) => all[i % all.length]);
}

export const PerformanceHistoryCharts: React.FC<PerformanceHistoryChartsProps> = ({
  historySlots,
  pageLabelForKey,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const vitalMsData = useMemo(
    () =>
      historySlots.map(({ date, metrics }) => {
        const row: Record<string, string | number | null> = {
          dateKey: date,
          dateLabel: format(parseISO(date), 'MMM d'),
        };
        for (const v of MS_WEB_VITALS) {
          row[v] = metrics?.webVitals?.[v]?.avg ?? null;
        }
        return row;
      }),
    [historySlots]
  );

  const clsData = useMemo(
    () =>
      historySlots.map(({ date, metrics }) => ({
        dateKey: date,
        dateLabel: format(parseISO(date), 'MMM d'),
        CLS: metrics?.webVitals?.CLS?.avg ?? null,
      })),
    [historySlots]
  );

  const sessionData = useMemo(
    () =>
      historySlots.map(({ date, metrics }) => ({
        dateKey: date,
        dateLabel: format(parseISO(date), 'MMM d'),
        sessions: metrics?.sessionCount ?? null,
      })),
    [historySlots]
  );

  const { pageLoadData, pageKeys } = useMemo(() => {
    const counts = new Map<string, number>();
    for (const { metrics } of historySlots) {
      if (!metrics?.pageLoads) continue;
      for (const [name, pl] of Object.entries(metrics.pageLoads)) {
        counts.set(name, (counts.get(name) ?? 0) + pl.count);
      }
    }
    const top = [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([k]) => k);

    const rows = historySlots.map(({ date, metrics }) => {
      const row: Record<string, string | number | null> = {
        dateKey: date,
        dateLabel: format(parseISO(date), 'MMM d'),
      };
      for (const key of top) {
        row[key] = metrics?.pageLoads?.[key]?.avgTimeMs ?? null;
      }
      return row;
    });
    return { pageLoadData: rows, pageKeys: top };
  }, [historySlots]);

  const hasAnyVitalMs = useMemo(
    () => vitalMsData.some((r) => MS_WEB_VITALS.some((v) => r[v] != null)),
    [vitalMsData]
  );
  const hasCls = useMemo(() => clsData.some((r) => r.CLS != null), [clsData]);
  const hasSessions = useMemo(() => sessionData.some((r) => r.sessions != null), [sessionData]);
  const hasPageLoads =
    pageKeys.length > 0 && pageLoadData.some((r) => pageKeys.some((k) => r[k] != null));

  const anyDayWithDoc = useMemo(() => historySlots.some((s) => s.metrics != null), [historySlots]);

  const strokeColors = useMemo(() => chartPalette(theme, 16), [theme]);

  const gridStroke = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const axisColor = theme.palette.text.secondary;

  const chartPaperSx = {
    p: 2,
    mb: 3,
    borderRadius: 2,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
  };

  const tooltipStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
    }),
    [theme.palette.background.paper, theme.palette.divider]
  );

  const formatTooltipDate = (payload: ReadonlyArray<{ payload?: { dateKey?: string } }> | undefined) => {
    const key = payload?.[0]?.payload?.dateKey;
    return key ? format(parseISO(key), 'MMM d, yyyy') : '';
  };

  if (!historySlots.length || !anyDayWithDoc) {
    return null;
  }

  if (!hasAnyVitalMs && !hasCls && !hasPageLoads && !hasSessions) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 0.5, fontWeight: 600, color: theme.palette.text.secondary }}
      >
        30-day history (line charts)
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 2, color: theme.palette.text.disabled }}>
        Daily averages from stored aggregates. Missing days break the line so you can see gaps in reporting.
      </Typography>

      {hasAnyVitalMs && (
        <Paper elevation={0} sx={chartPaperSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Core Web Vitals (ms)
          </Typography>
          <Box sx={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={vitalMsData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fill: axisColor, fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: axisColor, fontSize: 11 }}
                  width={48}
                  label={{
                    value: 'ms',
                    angle: -90,
                    position: 'insideLeft',
                    fill: axisColor,
                    style: { fontSize: 11 },
                  }}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={(_, payload) => formatTooltipDate(payload)}
                  formatter={(value: number) =>
                    value != null && !Number.isNaN(value) ? [`${Math.round(value)} ms`, ''] : ['—', '']
                  }
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {MS_WEB_VITALS.map((v, i) => (
                  <Line
                    key={v}
                    type="monotone"
                    dataKey={v}
                    name={v}
                    stroke={strokeColors[i]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {hasCls && (
        <Paper elevation={0} sx={chartPaperSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Cumulative Layout Shift (CLS)
          </Typography>
          <Box sx={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={clsData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fill: axisColor, fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} width={40} domain={['auto', 'auto']} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={(_, payload) => formatTooltipDate(payload)}
                  formatter={(value: number) =>
                    value != null && !Number.isNaN(value) ? [Number(value).toFixed(3), 'CLS'] : ['—', '']
                  }
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="CLS"
                  name="CLS"
                  stroke={strokeColors[MS_WEB_VITALS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {hasPageLoads && (
        <Paper elevation={0} sx={chartPaperSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Page load times (avg ms)
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.disabled }}>
            Up to 10 pages by sample volume in this window.
          </Typography>
          <Box sx={{ width: '100%', height: Math.min(320, 180 + pageKeys.length * 14) }}>
            <ResponsiveContainer>
              <LineChart data={pageLoadData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fill: axisColor, fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: axisColor, fontSize: 11 }}
                  width={48}
                  label={{
                    value: 'ms',
                    angle: -90,
                    position: 'insideLeft',
                    fill: axisColor,
                    style: { fontSize: 11 },
                  }}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={(_, payload) => formatTooltipDate(payload)}
                  formatter={(value: number, name: string) =>
                    value != null && !Number.isNaN(value)
                      ? [`${Math.round(value)} ms`, name]
                      : ['—', name]
                  }
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {pageKeys.map((key, i) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={pageLabelForKey(key)}
                    stroke={strokeColors[i % strokeColors.length]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}

      {hasSessions && (
        <Paper elevation={0} sx={chartPaperSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Sessions (daily aggregate)
          </Typography>
          <Box sx={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={sessionData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fill: axisColor, fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} width={44} allowDecimals={false} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={(_, payload) => formatTooltipDate(payload)}
                  formatter={(value: number) =>
                    value != null && !Number.isNaN(value) ? [Math.round(value), 'sessions'] : ['—', '']
                  }
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  name="Sessions"
                  stroke={strokeColors[3]}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
